import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_ACTIONS = [
  { label: 'List recent todos', message: 'List all my pending tasks' },
  { label: "What's overdue?", message: 'What tasks are overdue?' },
  { label: 'Summarize recent calls', message: 'Summarize my most recent conversations and key action items' },
  { label: "What needs attention?", message: "What should I focus on today? What needs my attention most?" },
];

/**
 * useChatState
 * Encapsulates all chat state and send logic.
 * Components just render the returned values — no business logic needed.
 */
export function useChatState() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(QUICK_ACTIONS.slice(0, 2));

  const sendMessage = useCallback(async (text) => {
    const msgText = (text || input).trim();
    if (!msgText || loading) return;

    const userMsg = { role: 'user', content: msgText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setIsOpen(true);

    try {
      // Cookies sent automatically via axios.defaults.withCredentials = true
      const res = await axios.post(`${API_BASE}/chat`, {
        message: msgText,
        history: messages.slice(-8),
      });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);

      if (res.data.suggestions?.length > 0) {
        setSuggestions(res.data.suggestions.map(s => ({ label: s, message: s })));
      }
    } catch (err) {
      console.error('Chat send failed:', err);
      toast.error('Chat failed — please try again');
      setMessages(prev => prev.slice(0, -1)); // remove failed message
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, messages, loading]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSuggestions(QUICK_ACTIONS.slice(0, 2));
  }, []);

  return {
    isOpen, setIsOpen,
    input, setInput,
    messages,
    loading,
    suggestions,
    sendMessage,
    clearChat,
    QUICK_ACTIONS,
  };
}
